import type React from 'react'
import {useUrlTab} from '@/hooks/useUrlTab'
import {URL_PARAMS, TAB_IDS} from '@/hooks/urlParams'
import ManageDashboards from './ManageDashboards/index'
import ManageSEMetricsKPI from './ManageSEMetricsKPI/index'
import ManageGlobalSEMetricsKPI from './ManageGlobalSEMetricsKPI/index'
import BaseCard from '@/components/common/BaseCard'
import BreadcrumbWrapper from '@/components/GDS/molecules/Breadcrumb/BreadcrumbWrapper'
import PageNavigationWrapper from '@/components/GDS/organisms/PageNavigation'
import {getNavigationOptions, getBreadcrumbItems} from './helpers/navigationOptions'

type NavOption = 'dashboards' | 'se-metrics'

// Map navigation option ids to their corresponding tab ids
const NAV_OPTION_TO_TAB: Record<string, string> = {
  '1': TAB_IDS.METRICS_SETTINGS.DASHBOARDS,
  '2': TAB_IDS.METRICS_SETTINGS.SE_METRICS,
  '3': TAB_IDS.METRICS_SETTINGS.GLOBAL_KPI,
}

const MetricsSettings: React.FC = () => {
  const {activeTab: activeNavItem, setActiveTab: setActiveNavItem} = useUrlTab(
    TAB_IDS.METRICS_SETTINGS.DASHBOARDS,
    URL_PARAMS.TAB,
  )

  // Get breadcrumb items from helper
  const breadcrumbItems = getBreadcrumbItems()

  // Get navigation options from helper
  const navigationOptions = getNavigationOptions()

  // Handle navigation item selection
  const handleNavigationChange = (optionId: string, childId?: string) => {
    const tabId = NAV_OPTION_TO_TAB[optionId]
    if (tabId) {
      setActiveNavItem(tabId)
    }
  }

  // Render content based on active navigation item
  const renderContent = () => {
    switch (activeNavItem) {
      case 'dashboards':
        return <ManageDashboards />
      case 'se-metrics':
        return <ManageSEMetricsKPI />
      case 'global-kpi':
        return <ManageGlobalSEMetricsKPI />
      default:
        return <ManageDashboards />
    }
  }

  return (
    <div className="flex h-full w-full flex-col gap-6 p-4">
      {/* Breadcrumb navigation */}
      <div className="mb-2">
        <BreadcrumbWrapper breadcrumbs={breadcrumbItems} />
      </div>

      <BaseCard title="Metrics Settings">
        <div className="flex w-full flex-col gap-6 md:flex-row">
          {/* Left sidebar with PageNavigationWrapper */}
          <div className="w-full gap-0.5 py-6 md:w-64 md:flex-shrink-0">
            <PageNavigationWrapper options={navigationOptions} onSelected={handleNavigationChange} selector={false} />
          </div>

          {/* Right content area */}
          <div className="min-w-0 flex-1 overflow-hidden">{renderContent()}</div>
        </div>
      </BaseCard>
    </div>
  )
}

export default MetricsSettings
